// Pure logic behind the Reply panel and the inbox chips (spec 2026-10-01 §3).
import type { ReplyActionView } from './replyActions'

export type StaffAction = {
  text: string
  resolution: 'done' | 'not_needed' | null
  resolved_by: string | null
  resolved_at: string | null
}

export type Check = { id: string; tool: string; input: unknown; result: unknown; refused: boolean }

export type Draft = {
  id: number
  status: 'drafting' | 'ready' | 'failed' | 'no_reply_needed' | 'sent' | 'discarded' | 'superseded'
  subject: string | null
  body_md: string | null
  category: string | null
  confidence: 'high' | 'medium' | 'low' | null
  facts: { text: string; check_id: string }[]
  unverified: string[]
  staff_actions: StaffAction[]
  promise_warnings: string[]
  checks: Check[]
  instruction: string | null
  error: string | null
  created_by: string
  created_at: string | null
  finished_at: string | null
  context_pruned_at: string | null
}

export type SentReply = {
  id: number; subject: string; body_md: string; to_email: string; sent_by: string
  edit_ratio: number | null; rewritten: boolean; created_at: string | null
  /** An editor sent it from the Editorial Desk's feedback popup. */
  from_editorial_desk?: boolean
}

export type InboundAttachment = {
  n: number; name: string; size: number; content_type: string; available: boolean; skipped: string | null
}

export type InboundMessage = {
  id: number; inbound_message_id: string; from_email: string; from_name: string | null
  subject: string | null; body_text: string; received_at: string; sender_mismatch: boolean
  auto_reply: boolean; spam_flag: boolean; attachments: InboundAttachment[]
}

export type TimelineEntry = {
  who: 'us' | 'customer'; at: string; text: string; key: string
  inbound?: InboundMessage; reply?: SentReply; sentBy?: string
}

export type Thread = {
  message: { publication: string; email: string | null; name: string | null; channel_label: string; subject?: string | null }
  can_reply: boolean
  current_draft: Draft | null
  drafts: Draft[]
  replies: SentReply[]
  inbound: InboundMessage[]
  actions?: ReplyActionView[]
}

function merged(us: TimelineEntry[], inbound: InboundMessage[] | undefined): TimelineEntry[] {
  const them = (inbound ?? []).filter((i) => !i.spam_flag).map((i) => ({ who: 'customer' as const,
    at: i.received_at, text: i.body_text, key: `i${i.id}`, inbound: i }))
  return [...us, ...them].sort((a, b) => a.at.localeCompare(b.at))
}

/** Our replies and the customer's answers, oldest first; spam is left out. */
export function timeline(thread: Thread): TimelineEntry[] {
  return merged(thread.replies.map((r) => ({ who: 'us' as const, at: r.created_at ?? '', text: r.body_md,
    key: `r${r.id}`, reply: r })), thread.inbound)
}

/** A reply as the legacy Contact list carries it (plain text body, no draft). */
export type LegacyReply = { id: number; body: string; sent_by: string; created_at: string | null }

/** The same timeline for the legacy Contact/Feedback blocks (Reply panel off). */
export function legacyTimeline(replies: LegacyReply[], inbound: InboundMessage[] | undefined): TimelineEntry[] {
  return merged(replies.map((r) => ({ who: 'us' as const, at: r.created_at ?? '', text: r.body,
    key: `r${r.id}`, sentBy: r.sent_by })), inbound)
}

/** How often the Reply panel re-reads its thread: fast while a draft runs,
 *  slower while the worker hasn't picked a new message up yet (it drafts
 *  within a minute), otherwise not at all. */
export function threadPollMs(thread: Thread | undefined): number | false {
  if (thread?.current_draft?.status === 'drafting') return 5000
  if (thread && thread.current_draft === null && thread.can_reply) return 15000
  return false
}

export type ReplyState = 'drafting' | 'ready' | 'no_reply_needed' | 'failed' | 'replied' | 'no_email' | 'customer_replied'

export function canSend(draft: Draft | null, subject: string, body: string): { ok: boolean; reason: string | null } {
  if (!subject.trim()) return { ok: false, reason: 'Write a subject.' }
  if (!body.trim()) return { ok: false, reason: 'Write a reply.' }
  if (draft && draft.staff_actions.some((a) => a.resolution === null)) {
    return { ok: false, reason: 'Mark each staff action Done or Not needed.' }
  }
  return { ok: true, reason: null }
}

const CHIPS: Record<ReplyState, { label: string; cls: string }> = {
  drafting: { label: 'Drafting…', cls: 'text-paper-trace border-ink-500' },
  ready: { label: 'Draft ready', cls: 'text-amber-glow border-amber-glow' },
  no_reply_needed: { label: 'No reply needed', cls: 'text-paper-dim border-ink-500' },
  failed: { label: 'Draft failed', cls: 'text-red-400 border-red-400' },
  replied: { label: 'Replied', cls: 'text-paper border-ink-500' },
  no_email: { label: 'No email', cls: 'text-paper-trace border-ink-500' },
  customer_replied: { label: 'Customer replied', cls: 'text-amber-glow border-amber-glow' },
}

export function chipFor(state: ReplyState | null | undefined): { label: string; cls: string } | null {
  return state ? CHIPS[state] : null
}

/** The subject the editor starts with: the draft's when it has one; for an emailed
 *  conversation with none, "Re: <their subject>" (never "Re: Re:"); otherwise blank. */
export function replySubject(channel: string, draftSubject: string | null | undefined,
  messageSubject: string | null | undefined): string {
  const drafted = (draftSubject ?? '').trim()
  if (drafted) return draftSubject!
  const theirs = (messageSubject ?? '').trim()
  if (channel !== 'email' || !theirs) return ''
  return /^re:/i.test(theirs) ? theirs : `Re: ${theirs}`
}
